/**
 * The first-use welcome (AIC-302; layout from the Codex prototype of 2026-09-29): a native <dialog> opened with
 * showModal(), as ConfirmDialog.tsx (inert page, top layer, focus contained; Esc, the close button and a backdrop
 * click close it). Not a confirm dialog: both choices are safe.
 *
 * It opens by itself at most once, and only when all of these hold: the server says "auto" (any signed-in user, of
 * any role, who hasn't seen it), /me and the route have loaded (the provider only exists inside the signed-in
 * shell, never on the auth callback or a permission failure), and no other dialog, lightbox or drawer is open
 * (it keeps checking while one is, and opens once the page has been quiet). Opening records
 * welcome_shown at once, so a close, a reload or a failed save never brings it back automatically. In preview, or
 * when progress can't be saved, it never opens by itself. Getting started's "View the welcome" opens it on request,
 * which records nothing.
 *
 * Design: the Sep 29 refinement in AIC-302 (the real IDC logo, "A clear next step. For every survey.", three phases,
 * "Show me around →"; a 40×40 close target whose 30×30 hover backing never changes its size). The body scrolls and
 * the footer stays in view on small screens.
 *
 * "Show me around" starts the tour (focus goes to its first step). "Explore on my own", Esc, the close button and the
 * backdrop close it, and focus goes to the Help link in the menu (else the page's <main>).
 */
import { createPortal } from "react-dom";
import { useEffect, useId, useRef, useState } from "react";
import { useReducedMotion } from "../components/motion.ts";
import idcLogo from "../assets/idc-logo.svg";
import { WELCOME, WELCOME_PHASES } from "./content.ts";
import { drawerOf } from "../screens/useDrawers.tsx";
import { useOnboarding } from "./OnboardingProvider.tsx";

/** The logo's own viewBox is 329×99: at 120px wide it is 36px tall, so it is never stretched. */
export const LOGO_W = 120;
export const LOGO_H = Math.round((LOGO_W * 99) / 329);

/** Focus the Help link if it is on screen (it is hidden on phones), else the page's main landmark. */
export function focusHelpOrMain(): void {
  const help = [...document.querySelectorAll<HTMLElement>("[data-onboarding='help-link']")].find((el) => !el.closest("[hidden]"));
  help?.focus();
  if (!help || document.activeElement !== help) document.getElementById("main")?.focus({ preventScroll: true });
}

/** What keeps the welcome from opening by itself: any dialog, lightbox or drawer on screen. */
const BUSY = "dialog[open], .ipd-lb-host, .ipd-drawer-host";
const RECHECK_MS = 120;
/** A drawer the URL's #hash names opens a moment after the page's data arrives: the welcome waits for it, but not for ever. */
const DRAWER_WAIT_MS = 5000;

export function Welcome({ path }: { path: string }) {
  const o = useOnboarding();
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDialogElement>(null);
  const first = useRef<HTMLButtonElement>(null);
  const shown = useRef(false);
  const returnTo = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState<"auto" | "manual" | null>(null);
  const id = useId();

  const eligible = o?.status === "saved" && o.view.welcome === "auto";
  useEffect(() => {
    if (!eligible || shown.current || !o) return;
    // It waits while anything else is open: a dialog, a lightbox or a drawer (AIC-503), including one a deep link opens a
    // moment after the page has loaded. Re-checked until the page has been quiet for two checks in a row, then it opens.
    let quiet = 0;
    const since = Date.now();
    let opened = false;
    const t = setInterval(() => {
      if (shown.current) return;
      if (document.querySelector(".ipd-drawer-host")) opened = true;
      if (document.querySelector(BUSY)) { quiet = 0; return; }
      // The URL names a drawer that hasn't opened yet (it opens when the page's data is in): wait for it (once it has been, the hash staying in the URL is nothing to wait for).
      if (!opened && drawerOf(window.location.hash) !== null && Date.now() - since < DRAWER_WAIT_MS) { quiet = 0; return; }
      if (++quiet < 2) return;
      shown.current = true;
      clearInterval(t);
      setOpen("auto");
      o.act({ type: "welcome_shown" });
    }, RECHECK_MS);
    return () => clearInterval(t);
  }, [eligible, o, path]);

  // A drawer or lightbox that opens while the automatic welcome is up (a deep link's, late) would leave this dialog, in the top
  // layer, over an inert page: the welcome steps aside instead. It was recorded as shown and doesn't come back by itself.
  useEffect(() => {
    if (open !== "auto") return;
    const t = setInterval(() => {
      if (!document.querySelector(".ipd-drawer-host, .ipd-lb-host")) return;
      clearInterval(t);
      o?.hideWelcome();
      setOpen(null);
    }, RECHECK_MS);
    return () => clearInterval(t);
  }, [open, o]);

  // "View the welcome" in Getting started.
  const requested = o?.welcomeRequests ?? 0;
  useEffect(() => {
    if (!requested) return;
    returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setOpen(o?.welcomeVisible ? "manual" : null);
  }, [requested, o?.welcomeVisible]);

  useEffect(() => {
    if (open === "manual" && path !== "/help") {
      setOpen(null);
      o?.hideWelcome();
      return;
    }
    if (open === "manual" && path === "/help") {
      document.getElementById(`${id}-inline-h`)?.focus();
      return;
    }
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
      // The tour button, unless it is disabled (preview): then the close button.
      (first.current && !first.current.disabled ? first.current : d.querySelector<HTMLElement>(".ob-close"))?.focus();
    } else if (!open && d.open) {
      if (typeof d.close === "function") d.close();
      else d.removeAttribute("open");
    }
  }, [open, path, id]);

  if (!o) return null;
  const explore = () => {
    const was = open;
    o.hideWelcome();
    setOpen(null);
    if (was === "auto") o.act({ type: "welcome_skipped" });
    const back = returnTo.current;
    returnTo.current = null;
    setTimeout(() => { if (was === "manual" && back?.isConnected) back.focus(); else focusHelpOrMain(); }, 0);
  };
  const tour = () => {
    const was = open;
    o.hideWelcome();
    setOpen(null);
    o.startTour(was === "auto" ? "welcome" : "help");
  };

  const helpSlot = path === "/help" && open === "manual" ? o.helpWelcomeHost : null;
  if (helpSlot) return createPortal(
    <section id="help-welcome-panel" className="ob-inline-welcome" aria-labelledby={`${id}-inline-h`}>
      <h2 id={`${id}-inline-h`} tabIndex={-1}>{WELCOME.title}</h2>
      <p>{WELCOME.body}</p>
      <ol>{WELCOME_PHASES.map((phase) => <li key={phase.n}><strong>{phase.title}</strong>{phase.body}</li>)}</ol>
      <p>{WELCOME.control}</p>
      <div className="ob-acts">
        <button className="ipd-btn ipd-btn--go" disabled={o.readOnly || o.status === "loading"} onClick={tour}>{WELCOME.tour}</button>
        <button className="ipd-btn" onClick={explore}>{WELCOME.explore}</button>
      </div>
    </section>, helpSlot);

  if (open === "manual" && path !== "/help") return null;

  return (
    <dialog
      ref={ref}
      className={`ipd-dialog ob-welcome${reduced ? " is-still" : ""}`}
      aria-labelledby={`${id}-h`}
      aria-describedby={`${id}-p`}
      onCancel={(e) => { e.preventDefault(); explore(); }}
      onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); explore(); } }}
      onClick={(e) => {
        if (e.target !== e.currentTarget) return;
        const r = e.currentTarget.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return;
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) explore();
      }}
    >
      {open ? (
        <>
          <div className="ob-welcome-body">
            <button type="button" className="ob-close" aria-label={WELCOME.close} onClick={explore}>
              {/* A symmetric 16×16 X, centred in the 40×40 target: never the × glyph, whose baseline sits low. */}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
                <path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            {/* The real IDC logo (assets/idc-logo.svg) in its own blue, 329:99, 120px wide. */}
            <img className="ob-logo" src={idcLogo} alt="IDC" width={LOGO_W} height={LOGO_H} />
            <p className="ob-kicker">{WELCOME.kicker}</p>
            <h2 id={`${id}-h`}><span>{WELCOME.titleLines[0]}</span> <span className="ob-line">{WELCOME.titleLines[1]}</span></h2>
            <p id={`${id}-p`} className="ob-lede">{WELCOME.body}</p>
            <ol className="ob-phases">
              {WELCOME_PHASES.map((p) => (
                <li key={p.n}><span className="ob-phase-n">{p.n}</span><h3>{p.title}</h3><p>{p.body}</p></li>
              ))}
            </ol>
            <p className="ob-control">{WELCOME.control}</p>
          </div>
          <div className="ob-welcome-foot">
            <button type="button" className="ipd-link" onClick={explore}>{WELCOME.explore}</button>
            <button ref={first} type="button" className="ipd-btn ipd-btn--go" disabled={o.readOnly} onClick={tour}>
              {WELCOME.tour}<span aria-hidden="true"> →</span>
            </button>
          </div>
        </>
      ) : null}
    </dialog>
  );
}
